import api from './api';

export type WalletSummaryResponse = {
  balance: number;
  totalDebits: number;
  totalCredits: number;
  lastActivityAt: string | null;
  activeDiscount: {
    id: string;
    type: string;
    percent: number;
    source: string;
    affiliateCode: string | null;
    appliedAt: string;
    shouldNotify: boolean;
  } | null;
};

export type WalletTransactionDirection = 'DEBIT' | 'CREDIT';

export type WalletTransaction = {
  id: string;
  amount: number;
  signedAmount: number;
  currency: string;
  originalAmount?: number | null;
  originalCurrency?: string | null;
  original_amount?: number | string | null;
  original_currency?: string | null;
  status: string;
  provider: string;
  transactionId: string | null;
  transactionType: string;
  transactionSource: string;
  orderId: string | null;
  direction: WalletTransactionDirection;
  createdAt: string;
  updatedAt: string;
};

export type WalletTransactionsResponse = {
  data: WalletTransaction[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

export const fetchWalletSummary = async (): Promise<WalletSummaryResponse> => {
  const { data } = await api.get<WalletSummaryResponse>('/wallet/summary');
  return data;
};

const normalizeOriginalAmount = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const sanitized = value.replace(/[^0-9.\-]/g, '');
    if (!sanitized) {
      return null;
    }
    const parsed = Number.parseFloat(sanitized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const fetchWalletTransactions = async (params: { page?: number; pageSize?: number } = {}): Promise<WalletTransactionsResponse> => {
  const { data } = await api.get<WalletTransactionsResponse>('/wallet/transactions', { params });
  return {
    ...data,
    data: data.data.map(txn => ({
      ...txn,
      originalAmount: txn.originalAmount ?? normalizeOriginalAmount(txn.original_amount),
      originalCurrency: txn.originalCurrency ?? txn.original_currency ?? null,
    })),
  };
};

export type WalletTopUpPayload = {
  amount: number;
  currency?: string;
  language?: string;
  orderId?: string;
};

export type WalletTopUpResponse = {
  data: {
    publicKey: string;
    data: string;
    signature: string;
    actionUrl: string;
  };
  paymentId: string;
  status: string;
  amount: number;
  currency: string;
};

export const createWalletTopUpCheckout = async (payload: WalletTopUpPayload): Promise<WalletTopUpResponse> => {
  const { data } = await api.get<WalletTopUpResponse>('/payments/liqpay/topup', {
    params: {
      amount: payload.amount,
      currency: payload.currency,
      language: payload.language,
      orderId: payload.orderId,
    },
  });
  return data;
};

export type LiqPayExchangeRate = {
  baseCurrency: string;
  quoteCurrency: string;
  rate: number;
  effectiveDate: string;
  source: string;
};

export const fetchLiqPayExchangeRate = async (baseCurrency: string, quoteCurrency: string): Promise<LiqPayExchangeRate> => {
  const { data } = await api.get<{ data: LiqPayExchangeRate }>('/payments/liqpay/exchange-rate', {
    params: {
      baseCurrency,
      quoteCurrency,
    },
  });
  return data.data;
};
